/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from 'react'
import TextField from '@mui/material/TextField'
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
} from '@mui/material'
import { useLang } from '~/hooks/useLang'
import useDispatchRegistrationPersonState from '../../../../stores/DispatchRegistrationPersonState'
import useDispatchRegistrationState from '../../../../stores/DispatchRegistrationState'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import {
  calculateAge,
  maskCPF,
  maskNames,
  maskPhoneNumber,
  removeMask,
  validate,
} from '../../../../utils/stringsManipulations'
import FieldAutocompleteRegistration from '~/components/FieldAutocompleteRegistration'
import { getDataByCpf, searchInvolvement } from '~/features/User/services'
import useSystemState from '~/features/System/stores/SystemState'
import { useSearchCPF as searchExternalCpf } from '~/features/User/components/SearchInvolvement'
import { useHookstate } from '@hookstate/core'
import SearchIcon from '@mui/icons-material/Search'
import FieldTextRegistration from '~/components/FieldTextRegistration'

interface FormPeoplePersonalDataProps {
  readonly disableFields?: boolean
}

export default function FormPeoplePersonalData({
  disableFields = false,
}: FormPeoplePersonalDataProps) {
  const { translate } = useLang()
  const { windowManager } = useSystemState()
  const { systemConfig } = useSystemState()

  const { registrationDispatch } = useDispatchRegistrationState()

  const { inputErrors } = registrationDispatch()

  const {
    registrationPersonDispatch,
    getDocumentTypes,
    getGenders,
    getEthnicities,
    getSchooling,
    getOccupation,
  } = useDispatchRegistrationPersonState()

  const { person } = registrationPersonDispatch()

  const [birthDateValue, setBirthDate] = useState<Dayjs | null>(null)
  const changeExternalCpf = searchExternalCpf().cpf()
  const resetCpfSearch = useHookstate(false)

  useEffect(() => {
    const cpfFormated = removeMask(person.get().cpf)

    if (cpfFormated.length == 11) {
      searchInvolvement({
        cpf: cpfFormated,
      }).then(({ data }) => {
        if (data.length > 0) {
          windowManager()['searchInvolvement'].open.set(true)
          changeExternalCpf.set(cpfFormated)
          windowManager()['searchInvolvement'].dataSearchInvolvement.set(data)
        } else {
          windowManager()['searchInvolvement'].open.set(false)
          windowManager()['searchInvolvement'].dataSearchInvolvement.set([])
        }
      })
      getDataByCpf({
        cpf: cpfFormated,
      }).then(async (data) => {
        if (data) {
          // Busca os objetos completos para os campos que vêm apenas com ID
          let gender = null
          let ethnicity = null

          if (data.gender_id) {
            const genders = await getGenders();
            gender = genders.find((genderItem) => genderItem.id === data.gender_id) || null;
          }
          if (data.ethnicity_id) {
            const ethnicities = await getEthnicities();
            ethnicity = ethnicities.find((ethnicityItem) => ethnicityItem.id === data.ethnicity_id) || null;
          }

          person.merge({
            name: data.name ?? '',
            nickname: data.nickname ?? '',
            motherName: data.mother_name ?? '',
            fatherName: data.father_name ?? '',
            phoneNumber: data.phone_number ? removeMask(data.phone_number) : '',
            birthDate: data.birth_date ?? null,
            age: data.age ?? '',
            gender,
            ethnicity,
            hasMentalIllness: !!data.has_mental_illness,
            hasPhysicalIllness: !!data.has_physical_illness,
            isLgbt: !!data.is_lgbt,
          })
        }
      }).catch((error) => {
        console.error('Erro ao buscar dados por CPF:', error);
      })
    } else {
      windowManager()['searchInvolvement'].open.set(false)
      windowManager()['searchInvolvement'].dataSearchInvolvement.set([])
    }

  }, [person.get().cpf, resetCpfSearch.get()])

  useEffect(() => {
    async function loadData() {
      const date = dayjs(person.get().birthDate, 'YYYY-MM-DD')
      setBirthDate(date.isValid() ? date : null)
      person.merge({
        birthDate: date.isValid() ? date.format('YYYY-MM-DD') : null,
      })
    }
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <FormControl fullWidth size="small">
      <Grid container direction={'row'}>
        <Grid item xs={3} sx={{ padding: 1 }}>
          <FieldAutocompleteRegistration
            label={translate('Gender')}
            onValueChange={(gender) => person.gender.set(gender)}
            valueId={person.gender.get()?.id as string}
            optionsFetch={getGenders}
            isRequired
            disabled={disableFields}
            error={inputErrors.gender.get()}
            onError={(value) => inputErrors.gender.set(value)}
          />
        </Grid>
        <Grid item xs={6} sx={{ padding: 1 }}>
          <FieldAutocompleteRegistration
            label={translate('Occupation')}
            onValueChange={(occupation) => person.occupation.set(occupation)}
            valueId={person.occupation.get()?.id as string}
            optionsFetch={getOccupation}
            isRequired={Boolean(
              parseInt(systemConfig().requireOccupationOnAssociatedPeople.get())
            )}
            disabled={disableFields}
            error={inputErrors.occupation.get()}
            onError={(value) => inputErrors.occupation.set(value)}
          />
        </Grid>
        <Grid item xs={3} sx={{ padding: 1 }}>
          <TextField
            disabled={disableFields}
            fullWidth
            error={!!inputErrors.get().cpf}
            size="small"
            id="outlined-cpf"
            label={translate('CPF')}
            onKeyDown={(event) => {
              if (event.code === 'NumpadEnter' || event.code === 'Enter') {
                resetCpfSearch.set(!resetCpfSearch.get())
              }
            }}
            value={person.get().cpf}
            InputProps={{
              onChange: (event) => {
                const cpf = maskCPF(event.target.value)
                person.merge({
                  cpf,
                })
                if (validate(cpf) || !cpf.length) {
                  inputErrors.cpf.set(false)
                } else {
                  inputErrors.cpf.set(true)
                }
              },
              endAdornment: (
                <IconButton
                  disabled={disableFields}
                  onClick={() => {
                    resetCpfSearch.set(!resetCpfSearch.get())
                  }}
                >
                  <SearchIcon />
                </IconButton>
              ),
            }}
          />
        </Grid>
        <Grid item xs={6} sx={{ padding: 1 }}>
          <FieldTextRegistration
            disableFields={disableFields}
            label={'Name'}
            value={person.name.get()}
            onChangeValue={(value) => person.name.set(value)}
          />
        </Grid>
        <Grid item xs={6} sx={{ padding: 1 }}>
          <FieldTextRegistration
            disableFields={disableFields}
            label={'Nickname'}
            value={person.nickname.get()}
            onChangeValue={(value) => person.nickname.set(value)}
          />
        </Grid>
        <Grid item xs={3} sx={{ padding: 1 }}>
          <FieldAutocompleteRegistration
            label={translate('Document Type')}
            onValueChange={(type) => {
              person.merge({
                otherDocument: type,
                otherDocumentType: '',
              })
              if (type?.id) {
                inputErrors.document.set(true)
              } else {
                inputErrors.document.set(false)
              }
            }}
            valueId={person.otherDocument.get()?.id as string}
            optionsFetch={getDocumentTypes}
            disabled={disableFields}
          />
        </Grid>
        <Grid item xs={3} sx={{ padding: 1 }}>
          <FieldTextRegistration
            disableFields={disableFields}
            label={'Document'}
            value={person.otherDocumentType.get()}
            onChangeValue={(value) => person.otherDocumentType.set(value)}
            inputErrors={inputErrors.document}
          />
        </Grid>
        <Grid item xs={3} sx={{ padding: 1 }}>
          <TextField
            disabled={disableFields}
            error={!!inputErrors.get().phoneNumber}
            onFocus={() => inputErrors.phoneNumber.set(false)}
            fullWidth
            size="small"
            id="outlined-phone-number"
            label={translate('Phone Number')}
            value={maskPhoneNumber(person.phoneNumber.get() || '')}
            onChange={(event) => {
              // Salva SEM máscara
              const phoneNumber = removeMask(event.target.value)
              person.phoneNumber.set(phoneNumber)
              if (phoneNumber.length > 0 && phoneNumber.length < 10) {
                inputErrors.phoneNumber.set(true)
              } else {
                inputErrors.phoneNumber.set(false)
              }
            }}
          />
        </Grid>
        <Grid item xs={3} sx={{ padding: 1 }}>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={'pt-br'}
          >
            <DateTimePicker
              disabled={disableFields}
              disableFuture
              views={['day', 'month', 'year']}
              minDate={dayjs().subtract(110, 'years')}
              label={translate(`Birth Date`)}
              format="DD/MM/YYYY"
              value={birthDateValue}
              onChange={(newValue: Dayjs | null) => {
                setBirthDate(newValue)

                if (!newValue) {
                  inputErrors.birthDate.set(false)
                  person.merge({
                    birthDate: null,
                  })
                  return
                }
                const age = calculateAge(newValue)
                if (age < 0 || age > 110) {
                  person.merge({
                    age: 0,
                  })
                  inputErrors.birthDate.set(true)
                } else {
                  person.merge({
                    age,
                    birthDate: newValue.format('YYYY-MM-DD'),
                  })
                  inputErrors.birthDate.set(false)
                }
              }}
              slotProps={{
                textField: {
                  error: !!inputErrors.get().birthDate,
                  size: 'small',
                  inputProps: {
                    placeholder: translate(`dd/mm/yyyy`),
                  },
                },
              }}
              // renderInput={(params) =>
              //   <TextField
              //     {...params}
              //     error={!!inputErrors.get().birthDate}
              //     size='small'
              //     inputProps={{
              //       ...params.inputProps,
              //       placeholder: translate(`dd/mm/yyyy`)
              //     }}
              //   />
              // }
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={1.25} sx={{ padding: 1 }}>
          <FieldTextRegistration
            disableFields={disableFields}
            label={'Age'}
            value={person.age.get()}
            onChangeValue={(value) => person.age.set(value)}
            type={'number'}
          />
        </Grid>
        <Grid item xs={3.75} sx={{ padding: 1 }}>
          <FieldAutocompleteRegistration
            label={translate('Ethnicity')}
            onValueChange={(ethnicity) => {
              person.merge({ ethnicity })
            }}
            valueId={person.ethnicity.get()?.id as string}
            optionsFetch={getEthnicities}
            disabled={disableFields}
          />
        </Grid>
        <Grid item xs={7} sx={{ padding: 1 }}>
          <FieldAutocompleteRegistration
            label={translate('Education')}
            onValueChange={(education) => {
              person.merge({ education })
            }}
            valueId={person.education.get()?.id as string}
            optionsFetch={getSchooling}
            disabled={disableFields}
          />
        </Grid>
        <Grid item xs={6} sx={{ padding: 1 }}>
          <FieldTextRegistration
            disableFields={disableFields}
            label={'Mother Name'}
            value={person.motherName.get()}
            onChangeValue={(value) => person.motherName.set(value)}
          />
        </Grid>
        <Grid item xs={6} sx={{ padding: 1 }}>
          <FieldTextRegistration
            disableFields={disableFields}
            label={'Father Name'}
            value={person.fatherName.get()}
            onChangeValue={(value) => person.fatherName.set(value)}
          />
        </Grid>
        <Grid item xs={12} sx={{ padding: 1 }}>
          <FormLabel component="legend">
{translate('Conditions')}
</FormLabel>
          <FormGroup aria-label="position" row>
            <FormControlLabel
              checked={person.get().hasMentalIllness}
              control={<Checkbox disabled={disableFields} />}
              label={translate('Mental Illness')}
              labelPlacement="end"
              onChange={(event: unknown, hasMentalIllness: boolean) => {
                person.merge({ hasMentalIllness })
              }}
            />
            <FormControlLabel
              checked={person.get().hasPhysicalIllness}
              control={<Checkbox disabled={disableFields} />}
              label={translate('Physical Illness')}
              labelPlacement="end"
              onChange={(event: unknown, hasPhysicalIllness: boolean) => {
                person.merge({ hasPhysicalIllness })
              }}
            />
            <FormControlLabel
              checked={person.get().isLgbt}
              control={<Checkbox disabled={disableFields} />}
              label={translate('LGBTQIAP+')}
              labelPlacement="end"
              onChange={(event: unknown, isLgbt: boolean) => {
                person.merge({ isLgbt })
              }}
            />
          </FormGroup>
        </Grid>
      </Grid>
    </FormControl>
  )
}
